import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { CTASection } from '../components/sections/CTASection';
import { ArrowUpRight } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { useServices } from '../hooks/useServices';
const tabs = ['All', 'Brand Identity & Graphic Design', 'Product Design', 'Software & System Development', 'Digital Growth & Strategy'];
const cardColors = ['bg-blue-50', 'bg-amber-50', 'bg-green-50', 'bg-purple-50', 'bg-rose-50', 'bg-cyan-50'];
function SkeletonCard() {
  return <div className="animate-pulse" data-id="element-1623">
      <div className="aspect-[4/3] rounded-xl bg-gray-200 mb-6" data-id="element-1624" />
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" data-id="element-1625" />
      <div className="h-4 bg-gray-100 rounded w-1/2 mb-4" data-id="element-1626" />
      <div className="flex gap-2" data-id="element-1627">
        <div className="h-6 w-16 bg-gray-100 rounded-full" data-id="element-1628" />
        <div className="h-6 w-20 bg-gray-100 rounded-full" data-id="element-1629" />
      </div>
    </div>;
}
export function WorkPage() {
  const [activeTab, setActiveTab] = useState('All');
  const {
    projects,
    loading,
    error
  } = useProjects();
  const {
    services
  } = useServices();
  // Build tabs dynamically from CMS services
  const tabs = ['All', ...services.map(s => s.title)];
  const filtered = activeTab === 'All' ? projects : projects.filter(p => p.category === activeTab);
  return <main className="pt-24" data-id="element-1630">
      {/* Hero */}
      <Section background="default" data-id="element-1631">
        <div className="max-w-4xl" data-id="element-1632">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-[#0C0C0C] mb-6 leading-tight" data-id="element-1633">
            Selected Work
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 leading-relaxed" data-id="element-1634">
            A collection of digital products, platforms, and experiences we've
            crafted for our clients.
          </motion.p>
        </div>
      </Section>

      {/* Tabs + Grid */}
      <Section background="white" data-id="element-1635">
        {/* Tab Bar */}
        <div className="flex flex-wrap gap-2 mb-12 border-b border-gray-100 pb-6" data-id="element-1636">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeTab === tab ? 'bg-[#00316B] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1637">
              {tab}
            </button>)}
        </div>

        {/* Loading Skeletons */}
        {loading && <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1638">
            {Array.from({
          length: 4
        }).map((_, i) => <SkeletonCard key={i} data-id="element-1639" />)}
          </div>}

        {/* Projects Grid */}
        {!loading && <AnimatePresence mode="wait" data-id="element-1640">
            <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1641">
              {filtered.length === 0 ? <div className="col-span-2 text-center py-20 text-gray-400" data-id="element-1642">
                  No projects in this category yet.
                </div> : filtered.map((project, index) => {
            const bgColor = cardColors[index % cardColors.length];
            const href = project.live_url || '#';
            return <motion.article key={project.id || project.title} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.08
            }} className="group cursor-pointer" data-id="element-1643">
                      <a href={href} target={project.live_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block" data-id="element-1644">
                        <div className={`aspect-[4/3] rounded-xl overflow-hidden mb-6 ${project.cover_image ? 'bg-gray-100' : bgColor} relative`} data-id="element-1645">
                          {project.cover_image ? <img src={project.cover_image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1646" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-1647">
                              <div className="w-3/4 h-3/4 bg-white/50 rounded-lg shadow-lg flex items-center justify-center" data-id="element-1648">
                                <span className="text-gray-400 text-sm" data-id="element-1649">
                                  Project Preview
                                </span>
                              </div>
                            </div>}
                          <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md" data-id="element-1650">
                            <ArrowUpRight className="w-5 h-5 text-[#0C0C0C]" data-id="element-1651" />
                          </div>
                        </div>

                        <div className="mb-3" data-id="element-1652">
                          <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] group-hover:text-[#00316B] transition-colors" data-id="element-1653">
                            {project.title}
                          </h2>
                          <p className="text-sm text-gray-500" data-id="element-1654">
                            {project.category}
                            {project.client_name ? ` · ${project.client_name}` : ''}
                          </p>
                        </div>

                        <p className="text-gray-600 mb-4 leading-relaxed" data-id="element-1655">
                          {project.description}
                        </p>

                        {project.tags.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1656">
                            {project.tags.map(tag => <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full" data-id="element-1657">
                                {tag}
                              </span>)}
                          </div>}
                      </a>
                    </motion.article>;
          })}
            </motion.div>
          </AnimatePresence>}
      </Section>

      <CTASection data-id="element-1658" />
    </main>;
}